import * as Sentry from '@sentry/nuxt'
import pickBy from 'lodash/pickBy'
import urljoin from 'url-join'

import pkg from '../../package.json'

import { useRuntimeConfig } from '#app'
import { defineNuxtPlugin } from '#imports'
import { useAuthToken } from '~/composables/auth/useAuthToken.js'
import { getErr998SentryExtra } from '~/utils/err998SentryExtra.js'

export default defineNuxtPlugin((nuxtApp) => {
  const { apiUrl, clientId, intranet } = useRuntimeConfig().public
  const { getAuthHeaders } = useAuthToken()

  let _errorHandler = null
  function setErrorHandler(handler) {
    _errorHandler = handler
  }

  // 清除 error handler，避免閉包持有舊的元件引用
  function clearErrorHandler() {
    _errorHandler = null
  }

  function isValid(value) {
    return value !== undefined && value !== null
  }

  // 送出前 token 閘門已於 request() 內恢復（bb76daa7e 曾移除、全交後端回 ERR_A103，導致無 token 請求
  // 照送並在後端產生大量 TOKEN_CHECK_FAIL）。現以後端回應形狀合成 ERR_A103 並餵入 _errorHandler，
  // 保留 TI-15375 的屏蔽/GA/靜默回大廳管線；後端 ERR_A103 仍為兜底（isPublic 漏標、SSR 等情境）。
  const noRetryErrorCodes = ['ERR_F108', 'ERR_G111', 'ERR_A224', 'ERR_P132']

  // 請求攔截器列表
  const requestInterceptors = []

  // 註冊攔截器
  const addRequestInterceptor = (interceptor) => {
    requestInterceptors.push(interceptor)
    // 返回移除函數
    return () => {
      const index = requestInterceptors.indexOf(interceptor)
      if (index > -1) {
        requestInterceptors.splice(index, 1)
      }
    }
  }

  // 清除所有攔截器，避免閉包累積造成記憶體洩漏
  const clearAllInterceptors = () => {
    requestInterceptors.length = 0
  }

  // 取得當前攔截器數量（用於除錯）
  const getInterceptorCount = () => requestInterceptors.length

  const request = async (options) => {
    let endpoint
    try {
      // 執行攔截器 - 允許修改 options 或進行其他處理
      for (const interceptor of requestInterceptors) {
        // 如果攔截器返回 false，則取消請求
        const result = interceptor(options)
        if (result === false) {
          const cancelError = new Error('Canceled')
          cancelError.name = 'CanceledError'
          cancelError.__isCancel = true
          throw cancelError
        }
      }

      const { url, method, params, isPublic, headers, isFormData = false, signal } = options
      const baseUrl =
        import.meta.server && intranet === '1' ? 'http://frontend-api-v2.default.svc.cluster.local' : nuxtApp.$getFastApiUrl?.() || apiUrl
      endpoint = urljoin(baseUrl, url.replace(':clientId', clientId))

      // 原子化快照：閘門判斷與實際送出使用同一次 header 讀取，
      // 避免 cookie ref 在守衛檢查與 $fetch 之間被 CookieStore 清空（TOCTOU）
      const authHeaders = getAuthHeaders()
      if (!isPublic && !authHeaders.Authorization) {
        const blockedError = { code: 'ERR_A103', name: 'TOKEN_NEEDED', message: 'No token provided' }
        _errorHandler && _errorHandler(blockedError)
        const gateError = new Error(blockedError.message)
        Object.assign(gateError, blockedError)
        gateError.url = endpoint
        throw gateError
      }

      const isGet = method.toLowerCase() === 'get'
      const payloadKey = isGet ? 'query' : 'body'
      const payload = isFormData ? params : pickBy(params, isValid)

      const res = await $fetch(endpoint, {
        method,
        headers: {
          'X-TH1GAMES-VERSION': `v${pkg.version}`,
          'X-TH1GAMES-PLATFORM': 'pwa',
          ...authHeaders,
          ...headers,
        },
        [payloadKey]: payload,
        retry: 2,
        retryDelay: 1000, // 重試延遲 1 秒（ofetch 會自動使用指數退避）
        retryStatusCodes: [408, 409, 425, 429, 500, 502, 504], // 需要重試的 HTTP 狀態碼
        signal, // 傳遞 signal 支援 AbortController
        onResponseError({ response }) {
          const code = response?._data?.error?.code
          // Explicitly stop ofetch retry when backend business error is in no-retry list.
          if (code && noRetryErrorCodes.includes(code)) {
            const noRetryError = new Error(response?._data?.error?.message || `No-retry error: ${code}`)
            noRetryError.data = response?._data
            throw noRetryError
          }
        },
      })

      if (res.data?.error) {
        const apiError = new Error(res.data.error.message || res.data.error.code || 'API Error')
        Object.assign(apiError, res.data.error)
        // 標記為後端業務錯誤（2xx body 帶 error payload，非 JS 回歸）：供 fire-and-forget 呼叫端辨識，
        // 避免已由 store fail 狀態呈現的業務錯誤再變成未捕捉 rejection 重複上報（CLIENT-PWA-SSR-16S）。
        // 本分支由 `if (res.data?.error)` 守門，error payload 必存在。
        apiError.isBusinessError = true
        throw apiError
      }
      return res
    } catch (e) {
      // 忽略各種形式的請求取消錯誤
      const isAbortError =
        e.__isCancel ||
        e.name === 'AbortError' ||
        e.name === 'CanceledError' ||
        e.message?.toLowerCase().includes('abort') ||
        e.message?.toLowerCase().includes('cancel')

      if (isAbortError) {
        console.log('[Request] Canceled or Aborted')
        return { data: {} }
      }

      if (e.message?.includes('Failed to fetch')) {
        // 只有在 UserInfo 已經被初始化的情況下才抓取 uidSite，避免插件初始化順序衝突
        const uidSite = nuxtApp._userInfoContext?.uidSite?.value
        const errData998 = getErr998SentryExtra({
          endpoint,
          method: options?.method,
          clientId,
          uidSite,
          clientIp: nuxtApp.$clientIp?.value,
        })
        // 改用去敏結構化欄位組標題並指定 fingerprint：避免含 appSecret 的完整 URL 進 Sentry 標題，
        // 並以 host+endpoint 分組，將「每 URL 一個 issue」收斂為「每 host+endpoint 一個」（issue #404 Option B）
        const sentryError = new Error(`ERR_998: [${errData998.method}] ${errData998.apiHost}${errData998.endpoint} <no response>`)
        sentryError.name = 'ERR_998'
        const redactedErrorMessage = e.message?.replace(/\?[^\s"]*/g, '?[REDACTED]')
        Sentry.captureException(sentryError, {
          level: 'warning',
          fingerprint: ['ERR_998', errData998.method, errData998.apiHost, errData998.endpoint],
          tags: errData998,
          extra: {
            ...errData998,
            errorMessage: redactedErrorMessage,
            hostTestResults: nuxtApp._hostTesterContext?.hostTester?.lastResults || [],
          },
        })
      } else if (e.data) {
        // ERR_A108 token 過期：交付錯誤處理器（屏蔽/登出）前先靜默重試一次，
        // 涵蓋 extendToken 輪替 token 的短暫競態，降低真正屏蔽的次數。
        if (e.data.error?.code === 'ERR_A108' && !options._a108Retried) {
          return await request({ ...options, _a108Retried: true })
        }
        _errorHandler && _errorHandler(e.data.error)
        const dataError = new Error(e.data.error?.message || e.data.error?.code || 'Request Error')
        Object.assign(dataError, e.data.error)
        // 僅在確有後端 error payload（e.data.error）時才標記為業務錯誤：此類已交付 _errorHandler 呈現，
        // fire-and-forget 可安全吞掉（CLIENT-PWA-SSR-16S）。反之 e.data 有值但缺 error 物件的
        // malformed / 契約異常回應，_errorHandler 收到 undefined 而不呈現，維持未標記以利上報、不遮蔽問題。
        if (e.data.error) dataError.isBusinessError = true
        throw dataError
      }
      throw e
    }
  }

  // SSR 請求結束時清理 interceptors 和 error handler
  // 避免閉包累積造成記憶體洩漏
  if (import.meta.server) {
    nuxtApp.hooks.hook('app:rendered', () => {
      clearAllInterceptors()
      clearErrorHandler()
    })
  }

  return {
    provide: {
      request: {
        send: request,
        setErrorHandler,
        addRequestInterceptor,
        clearErrorHandler,
        clearAllInterceptors,
        getInterceptorCount,
      },
    },
  }
})
